import {
  ConductEntry,
  GET_CONDUCT_ENTRIES_QUERY,
  GetConductEntriesQuerySort,
  GetConductEntriesParams,
  PagedResponse,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  CommentRowCell,
  DropdownComments,
  GridBody,
  MainPageGrid,
  SkeletonGridLoader,
  SkeletonRows,
} from '@schooly/style';
import { InfiniteData } from '@tanstack/react-query';
import { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import useAppLocation from '../../hooks/useAppLocation';
import { commentToDropdownCommentItem } from '../../utils/convertComments';
import { ConductEntriesHeader, ConductEntryRow, PAGE_SIZE, SKELETON_COLS } from './ConductGrid';
import { useConductComments } from './useConductComments';

export type ConductFlatQueryResult = {
  data: InfiniteData<PagedResponse<ConductEntry>> | undefined;
  isLoading: boolean;
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
  fetchNextPage: () => void;
  params: GetConductEntriesParams;
};

type ConductFlatGridProps = {
  schoolId: string;
  sort?: GetConductEntriesQuerySort;
  canEdit: boolean;
  onChangeSort: (v: GetConductEntriesQuerySort) => void;
  headerRightIcon?: ReactNode;
  queryResult: ConductFlatQueryResult;
};

export const ConductFlatGrid: FC<ConductFlatGridProps> = ({
  schoolId,
  canEdit,
  onChangeSort,
  sort,
  headerRightIcon,
  queryResult,
}) => {
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, params } = queryResult;
  const { currentStaff } = useAuth();
  const navigate = useNavigate();
  const location = useAppLocation();
  const [hoverId, setHoverId] = useState<string | null>(null);

  const total = data?.pages[0]?.count;

  const entries = useMemo(
    () => data?.pages.reduce<ConductEntry[]>((prev, curr) => [...prev, ...curr.results], []) ?? [],
    [data?.pages],
  );

  const queryKey = useMemo(() => [GET_CONDUCT_ENTRIES_QUERY, params], [params]);

  const { handleCommentAdd, handleCommentEdit, pendingEntriesId } = useConductComments({
    currentStaff,
    queryKey,
  });

  const onClickEntry = useCallback(
    (entry: ConductEntry) => {
      if (!canEdit) return;
      navigate(`/conduct/${entry.id}/edit`, {
        state: { backgroundLocation: location },
      });
    },
    [canEdit, location, navigate],
  );

  return (
    <MainPageGrid mt={2.4}>
      <ConductEntriesHeader sort={sort} onChangeSort={onChangeSort} rightIcon={headerRightIcon} />
      <GridBody>
        {entries.map((entry) => (
          <ConductEntryRow
            key={entry.id}
            schoolId={schoolId}
            entry={entry}
            hoverIdStudentId={hoverId}
            isEditable={canEdit}
            onClick={() => onClickEntry(entry)}
            onMouseEnter={() => setHoverId(entry.student.relation_id)}
            onMouseLeave={() => setHoverId(null)}
            commentsCellContent={
              <CommentRowCell rowStyleProps={{ my: -1 }}>
                {(onToggle, cellRef) => (
                  <DropdownComments
                    comments={entry.comments.map(commentToDropdownCommentItem)}
                    onAdd={handleCommentAdd(entry)}
                    onEdit={handleCommentEdit(entry)}
                    disabled={pendingEntriesId.has(entry.id)}
                    canAdd={canEdit}
                    canEditOwn={canEdit}
                    canEditOther={canEdit}
                    getParentRef={() => cellRef}
                    onToggle={(val) => {
                      setHoverId('');
                      onToggle(val);
                    }}
                    popoverMargin={2}
                    currentStaff={currentStaff}
                  />
                )}
              </CommentRowCell>
            }
          />
        ))}
        {isLoading && <SkeletonRows columnsCount={SKELETON_COLS} amount={PAGE_SIZE} />}
        <SkeletonGridLoader
          isFetching={isLoading || !!isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          columnsCount={SKELETON_COLS}
          amount={Math.min(
            PAGE_SIZE,
            total && data ? total - data.pages.length * PAGE_SIZE : PAGE_SIZE,
          )}
          threshold={0}
        />
      </GridBody>
    </MainPageGrid>
  );
};
